import { useMemo } from 'react'

import { useAccess } from '../../../../../context/useAccess'
import type { ItensPerUserLogged } from '../../../../../services/login/permissaoUsuarioLoado'

/**
 * Rotina do módulo "OS para Coleta" no cadastro de permissões
 * (mesmo cod usado em `Configs/Navegacao.ts` e no `rotina={32}` da versão em
 * produção). A permissão é do MÓDULO, não de cada tipo de coleta — todos os
 * submódulos compartilham esta rotina.
 */
export const ROTINA_OS_COLETA = 32

/** Permissão "tudo liberado", usada enquanto o contexto ainda não carregou. */
const PERMISSAO_PADRAO: Partial<ItensPerUserLogged> = {
    acesso: 1,
    alteracao: 1,
    inclusao: 1,
    exclusao: 1,
}

/**
 * Permissões do usuário logado para o módulo de OS.
 *
 * A versão em produção obtém isto via `<ViewPage onPermission={...} rotina={32}>`;
 * o módulo novo usa `DefautContainer`, que não trata permissão — daí este hook,
 * que lê a mesma fonte (`useAccess().permission`).
 *
 * Enquanto `permission` não chega, devolve tudo liberado: negar por padrão
 * esconderia os botões no primeiro render e faria a tela "piscar" as ações.
 */
export function usePermissoesOs(): Partial<ItensPerUserLogged> {
    const { permission } = useAccess()

    return useMemo(() => {
        const daRotina = (permission ?? []).find((item) => item?.cod_rotina === ROTINA_OS_COLETA)

        return daRotina ?? PERMISSAO_PADRAO
    }, [permission])
}

/**
 * Mensagem padrão para ações bloqueadas por permissão.
 *
 * As ações sem permissão ficam DESABILITADAS (não ocultas): esconder o botão faz
 * o usuário achar que o recurso não existe, enquanto o tooltip explica que falta
 * permissão — e a quem pedir.
 *
 * @param acao verbo no infinitivo, ex: 'criar OS', 'excluir análise'
 * @returns a mensagem, ou `undefined` quando a ação é permitida (não exibe tooltip)
 */
export function motivoSemPermissao(permitido: boolean, acao: string): string | undefined {
    return permitido ? undefined : `Você não tem permissão para ${acao}.`
}
